import React from 'react';
import CreatableSelect from 'react-select/creatable';
import InstitutionBadge from '../InstitutionBadge.jsx';
import AccountLabel from '../accounts/AccountLabel.jsx';

/**
 * A searchable picker for one of an import rule's text conditions, where only an exact spelling
 * matches: it offers the values imports have used, each with how many rows have it, and still takes
 * one typed in full ("Use …"). Blank is "any", which clearing it returns to.
 *
 * `options` are `{ value, label, count }` (see facetOptions()), or groups of them (`{ label, badge,
 * options }`, see accountRuleOptions()), in which case an option with an `identity` is drawn as the
 * budget's account (AccountLabel) with the name the rule matches on beneath it. It shares the
 * account pickers' class names, and so their look.
 */
export default function RuleValueSelect({
	options,
	value,
	onChange,
	placeholder,
	inputId,
	isDisabled = false,
	'aria-label': ariaLabel,
}) {
	const wanted = value || '';
	const flat = (options || []).flatMap((option) => option.options || [option]);
	const selected = wanted ? (flat.find((option) => option.value === wanted) || { value: wanted, label: wanted, count: null }) : null;

	return (
		<CreatableSelect
			className='account_select rule_value_select'
			classNamePrefix='account_select'
			inputId={inputId}
			aria-label={ariaLabel}
			options={options}
			value={selected}
			isDisabled={isDisabled}
			isClearable
			placeholder={placeholder}
			onChange={(option) => onChange(option ? String(option.value).trim() : '')}
			onCreateOption={(typed) => onChange(typed.trim())}
			isValidNewOption={(typed) => typed.trim() !== '' && !flat.some((option) => option.value === typed.trim())}
			formatCreateLabel={(typed) => <>Use &ldquo;{typed.trim()}&rdquo;</>}
			formatOptionLabel={(option, { context }) => <RuleValueLabel option={option} inMenu={context === 'menu'} />}
			formatGroupLabel={(group) => (
				<div className='account_select__group_heading_label'>
					{group.badge && <InstitutionBadge {...group.badge} size={16} />}
					<span>{group.label}</span>
				</div>
			)}
			menuPlacement='auto'
			maxMenuHeight={320}
			menuPortalTarget={typeof document === 'undefined' ? null : document.body}
			styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
		/>
	);
}

function RuleValueLabel({ option, inMenu }) {
	/** react-select's own "Use …" option has only a label. */
	if(option.__isNew__) {
		return <span>{option.label}</span>;
	}
	const count = inMenu && option.count !== null && option.count !== undefined
		? <span className='rule_value_count grey-text'>{option.count === 1 ? '1 row' : `${option.count} rows`}</span>
		: null;
	if(option.identity) {
		return (
			<div className='rule_value_option'>
				<div className='rule_value_account'>
					<AccountLabel identity={option.identity} compact={!inMenu} />
					{inMenu && <span className='rule_value_external grey-text'>{option.value}</span>}
				</div>
				{count}
			</div>
		);
	}
	return (
		<div className='rule_value_option'>
			<span className='rule_value_text'>
				{option.value}
				{inMenu && option.mapAction === 'skip' && <span className='grey-text'> &middot; skipped</span>}
			</span>
			{count}
		</div>
	);
}
